// src/views/admin/FieldOperationsPage.js
// FlowSense AI — Field Operations Command Center
// v4.0 | Enhanced UI, Advanced Animations, AI Elements, Enterprise-Ready
// Features: AI Dispatch, Live Technician Feed, Zone Analytics, Rule Engine,
//           ML Insights, KPI Dashboard, Bulk Dispatch, Audit Log, Performance Metrics
//           Glass Morphism UI, Framer Motion Animations, Real-time Pulse System
//           AI Confidence Scoring, Predictive Workload, Health Telemetry
//
// --- UPDATE (v4.1 · Zones / Constituencies live) ------------------------------------------
//   • New "Zones / Constituencies — live operations" command center (components/routing/
//     ZoneCommandCenter): every zone in its own colour on a live map with its work orders and
//     field staff, per-zone analytics (open / critical / overdue / unassigned, staff online,
//     demand per technician, risk score + reasons, 7-day forecast, spike detection, rule
//     coverage), FlowSense AI findings + dispatch suggestions (Assign uses the existing
//     PUT /workorders/:id/assign) + Explain / Ask FlowSense.
//   • A zone and a constituency are the same thing — labels say "Zones / constituencies".
//   • The page now listens to the WebSocket (field_ops_zone_update + work-order / location
//     events): the stat cards refresh within ~2 s of a change; the header shows the real
//     connection state instead of a static "Live".
//   • Finding actions open the matching management tab (zones, rules, teams, skills).
//   Existing panels, tabs and their logic are unchanged.
// --- END UPDATE ---------------------------------------------------------------------------

import React, {
    useState, useEffect, useRef, useMemo, useCallback, memo, lazy, Suspense,
} from 'react';
import {
    Box, Heading, Tabs, TabList, TabPanels, Tab, TabPanel,
    useColorModeValue, Badge, HStack, Icon, Text, VStack,
    Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex, IconButton,
    Tooltip, Menu, MenuButton, MenuList, MenuItem, MenuDivider,
    useToast, Stat, StatLabel, StatNumber, StatHelpText, StatArrow,
    SimpleGrid, Divider, Alert, AlertIcon, AlertDescription, AlertTitle,
    Progress, Drawer, DrawerBody, DrawerHeader, DrawerOverlay,
    DrawerContent, DrawerCloseButton, DrawerFooter, useDisclosure,
    Grid, Button, Select, Spinner, Collapse, useBreakpointValue,
    InputGroup, InputLeftElement, InputRightElement, Input,
    Avatar, Skeleton, SkeletonText, CircularProgress, CircularProgressLabel,
    Switch, useInterval, Tag, TagLabel, TagLeftIcon, Wrap, WrapItem,
    Center, Container, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    keyframes as ckKeyframes,
} from '@chakra-ui/react';
import {
    ChevronRightIcon, CheckIcon, SearchIcon, CloseIcon,
} from '@chakra-ui/icons';
import {
    FiDownload, FiPrinter, FiHelpCircle, FiInfo, FiActivity,
    FiZap, FiShield, FiCpu, FiWifi,
} from 'react-icons/fi';
import {
    MdBugReport, MdRefresh, MdMoreVert, MdDownload, MdHistory,
    MdTrendingUp, MdGroups, MdLocationOn, MdRule, MdEngineering,
    MdDashboard, MdCheckCircle, MdExpandMore, MdExpandLess,
    MdFlashOn, MdSmartToy, MdWarning, MdNetworkCheck,
    MdOutlineAutoAwesome, MdPerson, MdFiberManualRecord,
    MdSchedule, MdSend, MdBolt, MdAnalytics, MdBarChart,
    MdInsights, MdError, MdInfo, MdHistory as MdAudit,
    MdCheckBox, MdCheckBoxOutlineBlank, MdLibraryAddCheck,
    MdViewList, MdViewModule, MdSpeed, MdMemory, MdStorage,
    MdAutorenew, MdPsychology, MdModelTraining, MdTimeline,
    MdNotificationsActive, MdTrendingDown, MdTrendingFlat,
    MdOutlineWaterDrop, MdFilterList,
} from 'react-icons/md';
import { motion, AnimatePresence, useAnimation, useInView } from 'framer-motion';

import TeamManagementComponent       from '../../components/routing/TeamManagementComponent';
import ZoneManagementComponent       from '../../components/routing/ZoneManagementComponent';
import AssignmentRuleManagementComponent from '../../components/routing/AssignmentRuleManagementComponent';
import TechnicianSkillsManager        from '../../components/routing/TechnicianSkillsManager';
import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';
// --- UPDATE
import ZoneCommandCenter from '../../components/routing/ZoneCommandCenter';
import webSocketService from '../../services/websocketService';
import { LIVE_EVENTS } from '../../hooks/useFieldOpsZones';
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionGrid = motion(Grid);
const MotionText = motion(Text);

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION PRESETS — reusable motion variants
// ─────────────────────────────────────────────────────────────────────────────
const ANIM = {
    fadeInUp:     { initial:{ opacity:0, y:20 },  animate:{ opacity:1, y:0 } },
    fadeInDown:   { initial:{ opacity:0, y:-20 }, animate:{ opacity:1, y:0 } },
    fadeInLeft:   { initial:{ opacity:0, x:-20 }, animate:{ opacity:1, x:0 } },
    fadeInRight:  { initial:{ opacity:0, x:20 },  animate:{ opacity:1, x:0 } },
    scaleIn:      { initial:{ opacity:0, scale:0.85 }, animate:{ opacity:1, scale:1 } },
    slideIn:      { initial:{ opacity:0, x:-12 }, animate:{ opacity:1, x:0 } },
    stagger:      (i, base=0.04) => ({ delay: i * base }),
    springBounce: { type:'spring', stiffness:300, damping:24 },
    smoothEase:   { duration:0.35, ease:[0.25, 0.46, 0.45, 0.94] },
};

// ─────────────────────────────────────────────────────────────────────────────
// GLASSMORPHISM HELPER
// ─────────────────────────────────────────────────────────────────────────────
const GLASS = {
    light: {
        bg: 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(16px) saturate(180%)',
        border: '1px solid rgba(255,255,255,0.35)',
    },
    dark: {
        bg: 'rgba(26,32,44,0.72)',
        backdropFilter: 'blur(16px) saturate(180%)',
        border: '1px solid rgba(255,255,255,0.08)',
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// API SERVICE LAYER
// ─────────────────────────────────────────────────────────────────────────────
// STRICT .ENV ENFORCEMENT: 
// It will prioritize the REACT_APP_API_URL from your .env file, 
// fallback to your config.js, and finally default to localhost:3001.
const API_BASE = config.apiUrl;

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken() || ''}`,
});

const apiFetch = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, { headers: authHeaders(), ...opts })
        .then(async r => {
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.message || `HTTP ${r.status}`);
            return json;
        });

const fieldOpsApi = {
    getStats:         ()        => apiFetch('/field-ops/stats'),
    getSystemHealth:  ()        => apiFetch('/field-ops/health'),
    getTechnicians:   ()        => apiFetch('/field-ops/technicians'),
    getTeams:         ()        => apiFetch('/field-ops/teams'),
    autoDispatch:     (payload) => apiFetch('/field-ops/auto-dispatch', { method:'POST', body:JSON.stringify(payload) }),
    getDispatchQueue: ()        => apiFetch('/field-ops/dispatch-queue'),
    getWorkOrders:    (p={})    => apiFetch(`/workorders?${new URLSearchParams({ limit:20, ...p })}`),
    getAiInsights:    ()        => apiFetch('/analytics/ai-insights'),
    getPerformance: (params={}) => apiFetch(`/analytics/field-ops-performance?${new URLSearchParams(params)}`),
    getAuditLog:      (n=30)    => apiFetch(`/analytics/audit-log?limit=${n}&module=field-ops`),
    assignWorkOrder:  (id, uid) => apiFetch(`/workorders/${id}/assign`, { method:'PUT', body:JSON.stringify({ userId:uid }) }),
    updateWOStatus:   (id, s)   => apiFetch(`/workorders/${id}/status`,  { method:'PUT', body:JSON.stringify({ status:s  }) }),
};

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG LOGGER (enhanced with performance tracking)
// ─────────────────────────────────────────────────────────────────────────────
class DebugLogger {
    constructor(name) {
        this.name      = name;
        this.logs      = [];
        this.listeners = [];
        this.enabled   = process.env.NODE_ENV === 'development';
        this._perfMarks = {};
    }
    log(level, message, data = null) {
        if (!this.enabled) return;
        const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
            timestamp: new Date().toISOString(),
            level, message, data, component: this.name,
        };
        this.logs.push(entry);
        if (this.logs.length > 500) this.logs.shift();
        this.listeners.forEach(l => l(entry));
        const s = {
            debug:'color:#888;font-size:11px', info:'color:#3182ce;font-weight:bold',
            warn:'color:#d69e2e;font-weight:bold', error:'color:#e53e3e;font-weight:bold',
            success:'color:#38a169;font-weight:bold',
        };
        if (data) console.log(`%c[${this.name}][${level.toUpperCase()}] ${message}`, s[level]||s.info, data);
        else      console.log(`%c[${this.name}][${level.toUpperCase()}] ${message}`, s[level]||s.info);
    }
    debug  (m,d){ this.log('debug',  m,d); }
    info   (m,d){ this.log('info',   m,d); }
    warn   (m,d){ this.log('warn',   m,d); }
    error  (m,d){ this.log('error',  m,d); }
    success(m,d){ this.log('success',m,d); }
    perfStart(label){ this._perfMarks[label] = performance.now(); }
    perfEnd(label){ 
        const start = this._perfMarks[label];
        if (start) {
            const dur = (performance.now() - start).toFixed(1);
            this.info(`⏱ ${label}: ${dur}ms`);
            delete this._perfMarks[label];
            return parseFloat(dur);
        }
        return 0;
    }
    subscribe(cb){ this.listeners.push(cb); return () => { this.listeners = this.listeners.filter(l=>l!==cb); }; }
    getLogs(lv=null){ return lv ? this.logs.filter(l=>l.level===lv) : [...this.logs]; }
    clear(){ this.logs=[]; }
    export(){ return JSON.stringify(this.logs, null, 2); }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const PRIORITY_CFG = {
    critical:{ color:'red',    label:'Critical', weight:4 },
    high:    { color:'orange', label:'High',     weight:3 },
    medium:  { color:'yellow', label:'Medium',   weight:2 },
    low:     { color:'green',  label:'Low',      weight:1 },
};
const STATUS_CFG = {
    active:  { color:'green',  label:'Available', emoji:'🟢' },
    busy:    { color:'orange', label:'On Job',    emoji:'🟠' },
    offline: { color:'gray',   label:'Offline',   emoji:'⚪' },
    inactive:{ color:'red',    label:'Inactive',  emoji:'🔴' },
};
const INSIGHT_CFG = {
    warning:{ color:'orange', icon:MdWarning       },
    error:  { color:'red',    icon:MdError         },
    success:{ color:'green',  icon:MdCheckCircle   },
    info:   { color:'blue',   icon:MdSmartToy      },
};

// ─────────────────────────────────────────────────────────────────────────────
// SMALL SHARED COMPONENTS (Enhanced with animations + glass morphism)
// ─────────────────────────────────────────────────────────────────────────────

const PulseIndicator = ({ color='green', size=3 }) => (
    <Box position="relative" display="inline-flex"
        w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox
            position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.9,1], opacity:[0.8,0,0.8] }}
            transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }}
        />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

const AIStatusBadge = ({ status }) => {
    const map = {
        idle:      { color:'gray',  label:'AI Idle',    spin:false, glow:false },
        thinking:  { color:'blue',  label:'Analyzing…', spin:true,  glow:true  },
        dispatched:{ color:'green', label:'Dispatched', spin:false, glow:true  },
        error:     { color:'red',   label:'Error',      spin:false, glow:false },
    };
    const c = map[status] || map.idle;
    return (
        <MotionBox
            animate={c.glow ? { boxShadow:[
                `0 0 0px var(--chakra-colors-${c.color}-400)`,
                `0 0 12px var(--chakra-colors-${c.color}-400)`,
                `0 0 0px var(--chakra-colors-${c.color}-400)`,
            ] } : {}}
            transition={{ duration:2, repeat:Infinity }}
            borderRadius="full"
        >
            <Badge colorScheme={c.color} px={3} py={1} borderRadius="full"
                display="inline-flex" alignItems="center" gap={2}
                boxShadow={c.glow ? `0 0 8px var(--chakra-colors-${c.color}-300)` : 'none'}>
                {c.spin && <Spinner size="xs" mr={1} />}
                <Text fontSize="xs" fontWeight="700">{c.label}</Text>
            </Badge>
        </MotionBox>
    );
};

/** AI Confidence Bar — visual confidence indicator for AI decisions */
const AIConfidenceBar = ({ confidence=0, label='Confidence', size='sm', showLabel=true }) => {
    const getConfColor = (val) => {
        if (val >= 90) return 'green';
        if (val >= 70) return 'blue';
        if (val >= 50) return 'yellow';
        return 'red';
    };
    const color = getConfColor(confidence);
    const barBg = useColorModeValue('gray.100','gray.700');
    return (
        <Box w="full">
            {showLabel && (
                <HStack justify="space-between" mb={1}>
                    <Text fontSize="2xs" color="gray.500" fontWeight="600">{label}</Text>
                    <Text fontSize="2xs" fontWeight="800" color={`${color}.500`}>{confidence}%</Text>
                </HStack>
            )}
            <Progress
                value={confidence} size={size} colorScheme={color}
                borderRadius="full" bg={barBg}
                hasStripe={confidence >= 70} isAnimated={confidence >= 70}
            />
        </Box>
    );
};

/** Shimmer Loading Skeleton — premium loading effect */
const ShimmerSkeleton = ({ lines=3, ...props }) => {
    const startColor = useColorModeValue('gray.100','gray.700');
    const endColor = useColorModeValue('gray.300','gray.600');

    return (
        <VStack spacing={2} w="full" {...props}>
            {Array.from({ length: lines }, (_, i) => (
                <Skeleton
                    key={i} h="16px" w={i === lines - 1 ? '60%' : '100%'}
                    borderRadius="md" startColor={startColor} endColor={endColor}
                />
            ))}
        </VStack>
    );
};

const PriorityDot = ({ priority }) => {
    const pc = PRIORITY_CFG[priority] || PRIORITY_CFG.medium;
    return (
        <MotionBox
            w={2} h={2} borderRadius="full" bg={`${pc.color}.500`} flexShrink={0}
            animate={priority === 'critical' ? { scale:[1,1.4,1], opacity:[1,0.7,1] } : {}}
            transition={{ duration:1.5, repeat:Infinity }}
        />
    );
};

/** Empty state placeholder */
const EmptyState = ({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox
            animate={{ y:[0,-6,0] }}
            transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}
        >
            <Icon as={IconComp} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400" fontSize="sm">{title}</Text>
        {subtitle && <Text fontSize="xs" color="gray.400" textAlign="center" maxW="240px">{subtitle}</Text>}
    </Flex>
);

// GlassCard — enhanced card with glass morphism effect
const GlassCard = memo(({ children, accentColor, hoverLift=true, ...rest }) => {
    const glassBg     = useColorModeValue('rgba(255,255,255,0.78)','rgba(26,32,44,0.78)');
    const glassBorder = useColorModeValue('rgba(255,255,255,0.4)','rgba(255,255,255,0.08)');
    const borderColor = useColorModeValue('gray.200','gray.700');
    return (
        <MotionBox
            whileHover={hoverLift ? { y:-3, boxShadow:'0 20px 50px rgba(0,0,0,0.12)' } : {}}
            transition={ANIM.smoothEase}
        >
            <Box
                bg={glassBg}
                backdropFilter="blur(16px) saturate(180%)"
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={borderColor}
                overflow="hidden"
                position="relative"
                transition="all 0.3s"
                {...rest}
            >
                {accentColor && (
                    <Box position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${accentColor}.400, ${accentColor}.600)`}
                        borderTopRadius="2xl" />
                )}
                {children}
            </Box>
        </MotionBox>
    );
});

// SectionCard — wraps panels in a consistent card style (enhanced)
const SectionCard = memo(({ children, accentColor, ...rest }) => {
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.700');
    return (
        <MotionBox
            whileHover={{ y:-2, boxShadow:'0 16px 40px rgba(0,0,0,0.1)' }}
            transition={ANIM.smoothEase}
        >
            <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
                borderColor={borderColor} overflow="hidden"
                position="relative" transition="all 0.3s" {...rest}>
                {accentColor && (
                    <Box position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${accentColor}.400, ${accentColor}.600)`}
                        borderTopRadius="2xl" />
                )}
                {children}
            </Box>
        </MotionBox>
    );
});

// PanelHeader — coloured icon header strip (enhanced with gradient backgrounds)
const PanelHeader = memo(({ iconBg, icon:IconComp, title, subtitle, right }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <Flex px={5} py={4} borderBottomWidth="1px" borderColor={borderColor}
            align="center" justify="space-between">
            <HStack spacing={3}>
                <MotionBox
                    whileHover={{ rotate:8, scale:1.1 }}
                    transition={{ type:'spring', stiffness:400 }}
                >
                    <Box bg={iconBg} p={2.5} borderRadius="xl"
                        boxShadow={`0 4px 14px var(--chakra-colors-${iconBg?.split('.')[0] || 'blue'}-300)`}>
                        <Icon as={IconComp} color="white" boxSize={5} />
                    </Box>
                </MotionBox>
                <Box>
                    <Text fontWeight="800" fontSize="sm">{title}</Text>
                    {subtitle && <Text fontSize="xs" color={mutedColor}>{subtitle}</Text>}
                </Box>
            </HStack>
            {right}
        </Flex>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARD (Enhanced with animated counter + gradient hover)
// ─────────────────────────────────────────────────────────────────────────────
const StatCard = memo(({ label, value, icon:IconComp, color='blue', helpText,
    isLoading, onClick, isActive, liveIndicator, delta }) => {
    const cardBg       = useColorModeValue('white','gray.800');
    const borderColor  = useColorModeValue('gray.200','gray.700');
    const labelColor   = useColorModeValue('gray.500','gray.400');
    const valueColor   = useColorModeValue('gray.800','white');
    const iconBg       = useColorModeValue(`${color}.50`, `${color}.900`);
    const iconColor    = useColorModeValue(`${color}.600`,`${color}.300`);
    const activeBorder = useColorModeValue(`${color}.400`,`${color}.500`);
    const hoverGradBg  = useColorModeValue(`${color}.50`,`${color}.900`);

    return (
        <MotionBox
            whileHover={{ y:-4, boxShadow:'0 16px 40px rgba(0,0,0,0.14)' }}
            whileTap={onClick ? { scale:0.97 } : {}}
            transition={ANIM.smoothEase}
            cursor={onClick ? 'pointer' : 'default'}
            onClick={onClick}
        >
            <Box
                bg={cardBg} p={5} borderRadius="2xl" borderWidth="2px"
                borderColor={isActive ? activeBorder : borderColor}
                boxShadow={isActive ? `0 0 0 4px var(--chakra-colors-${color}-100)` : 'md'}
                position="relative" overflow="hidden" transition="all 0.3s"
                _hover={{ borderColor:`${color}.300` }}
            >
                {/* Active top accent bar */}
                {isActive && (
                    <MotionBox
                        layoutId="statCardAccent"
                        position="absolute" top={0} left={0} right={0} h="3px"
                        bgGradient={`linear(to-r, ${color}.400, ${color}.700)`}
                        borderTopRadius="2xl"
                    />
                )}
                {/* Ambient glow */}
                <Box position="absolute" top="-12px" right="-12px" w="80px" h="80px"
                    bg={iconBg} opacity={0.5} borderRadius="full" filter="blur(14px)"
                    transition="all 0.4s"
                />
                {/* Hover gradient overlay */}
                <Box position="absolute" inset={0} opacity={0}
                    bgGradient={`linear(to-br, transparent, ${hoverGradBg})`}
                    transition="opacity 0.3s" _groupHover={{ opacity:0.5 }}
                    pointerEvents="none" borderRadius="2xl"
                />
                <Flex justify="space-between" align="flex-start">
                    <Box flex={1}>
                        <HStack spacing={2} mb={1}>
                            <Text fontSize="xs" fontWeight="700" color={labelColor}
                                textTransform="uppercase" letterSpacing="wider">
                                {label}
                            </Text>
                            {liveIndicator && <PulseIndicator color={color} size={2} />}
                        </HStack>
                        {isLoading ? (
                            <Skeleton h="38px" w="70px" borderRadius="lg" mt={1} />
                        ) : (
                            <HStack spacing={2} align="baseline" mt={0.5}>
                                <MotionBox
                                    key={value}
                                    initial={{ opacity:0, y:10 }}
                                    animate={{ opacity:1, y:0 }}
                                    transition={{ type:'spring', stiffness:200, damping:20 }}
                                >
                                    <Text fontSize="3xl" fontWeight="900" color={valueColor} lineHeight="1">
                                        {value ?? '—'}
                                    </Text>
                                </MotionBox>
                                {delta !== undefined && delta !== 0 && (
                                    <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.springBounce}>
                                        <Badge colorScheme={delta > 0 ? 'green' : 'red'} fontSize="2xs" borderRadius="full"
                                            display="flex" alignItems="center" gap={0.5}>
                                            <Icon as={delta > 0 ? MdTrendingUp : MdTrendingDown} boxSize={3} />
                                            {delta > 0 ? '+' : ''}{delta}
                                        </Badge>
                                    </MotionBox>
                                )}
                            </HStack>
                        )}
                        {helpText && (
                            <Text fontSize="2xs" color={labelColor} mt={1}>{helpText}</Text>
                        )}
                    </Box>
                    <MotionBox
                        whileHover={{ rotate:12, scale:1.1 }}
                        transition={{ type:'spring', stiffness:400 }}
                    >
                        <Box bg={iconBg} p={3} borderRadius="xl"
                            boxShadow={`0 4px 14px var(--chakra-colors-${color}-200)`}>
                            <Icon as={IconComp} boxSize={6} color={iconColor} />
                        </Box>
                    </MotionBox>
                </Flex>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// DISPATCH QUEUE ROW — enhanced with ripple + confidence
// ─────────────────────────────────────────────────────────────────────────────
const DispatchQueueRow = memo(({ wo, index, isSelected, onToggle, onDispatch, isDispatching }) => {
    const borderColor  = useColorModeValue('gray.200','gray.700');
    const rowHover     = useColorModeValue('blue.50','blue.900');
    const rowSelected  = useColorModeValue('blue.100','blue.800');
    const mutedColor   = useColorModeValue('gray.500','gray.400');
    const pc = PRIORITY_CFG[wo.priority] || PRIORITY_CFG.medium;

    return (
        <MotionBox
            {...ANIM.slideIn} transition={{ ...ANIM.stagger(index) }}
        >
            <Flex
                p={3} borderRadius="xl" borderWidth="1px"
                borderColor={isSelected ? 'blue.400' : borderColor}
                bg={isSelected ? rowSelected : 'transparent'}
                _hover={{ bg: isSelected ? rowSelected : rowHover, borderColor:'blue.300',
                    transform:'translateX(2px)' }}
                transition="all 0.2s" align="center" gap={2}
                cursor="pointer" onClick={() => onToggle(wo.id)}
                role="group"
            >
                <MotionBox
                    animate={isSelected ? { scale:[1,1.2,1] } : {}}
                    transition={{ duration:0.3 }}
                >
                    <Icon
                        as={isSelected ? MdCheckBox : MdCheckBoxOutlineBlank}
                        boxSize={4} color={isSelected ? 'blue.500' : 'gray.400'}
                        flexShrink={0}
                    />
                </MotionBox>
                <PriorityDot priority={wo.priority} />
                <Box flex={1} minW={0}>
                    <Text fontSize="xs" fontWeight="700" noOfLines={1}>
                        {wo.title || 'Untitled Work Order'}
                    </Text>
                    <HStack spacing={2} mt={0.5} flexWrap="wrap">
                        <Badge colorScheme={pc.color} fontSize="2xs" borderRadius="full">
                            {pc.label}
                        </Badge>
                        {wo.due_date && (
                            <HStack spacing={0.5}>
                                <Icon as={MdSchedule} boxSize={2.5} color={mutedColor} />
                                <Text fontSize="2xs" color={mutedColor}>
                                    {new Date(wo.due_date).toLocaleDateString()}
                                </Text>
                            </HStack>
                        )}
                        {wo.asset_id && (
                            <Text fontSize="2xs" color={mutedColor} noOfLines={1} fontFamily="mono">
                                #{wo.asset_id.slice(0,8)}
                            </Text>
                        )}
                    </HStack>
                </Box>
                <Tooltip label="AI Auto-Dispatch" hasArrow placement="left">
                    <IconButton
                        icon={isDispatching ? <Spinner size="xs" /> : <Icon as={MdFlashOn} />}
                        size="xs" colorScheme="blue" borderRadius="full"
                        isLoading={isDispatching}
                        onClick={e => { e.stopPropagation(); onDispatch(wo); }}
                        aria-label="Dispatch"
                        _hover={{ transform:'scale(1.15)', boxShadow:'0 0 12px var(--chakra-colors-blue-400)' }}
                        transition="all 0.2s"
                    />
                </Tooltip>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// AI DISPATCH PANEL (Enhanced with AI confidence + animated result)
// ─────────────────────────────────────────────────────────────────────────────
const AIDispatchPanel = ({ onDispatch, logger }) => {
    const [dispatchStatus, setDispatchStatus] = useState('idle');
    const [dispatchResult, setDispatchResult] = useState(null);
    const [queue,          setQueue]          = useState([]);
    const [isLoadingQueue, setIsLoadingQueue] = useState(true);
    const [selected,       setSelected]       = useState(new Set());
    const [isBulkRunning,  setIsBulkRunning]  = useState(false);
    const [searchTerm,     setSearchTerm]     = useState('');
    const [filterPriority, setFilterPriority] = useState('all');

    const searchBg    = useColorModeValue('gray.50','gray.700');
    const toast       = useToast();

    const loadQueue = useCallback(async () => {
        setIsLoadingQueue(true);
        try {
            const res = await fieldOpsApi.getDispatchQueue();
            setQueue(res?.data || []);
            logger?.info('Queue loaded', { count: res?.data?.length });
        } catch (err) {
            logger?.error('Queue load failed', err);
            setQueue([]);
        } finally {
            setIsLoadingQueue(false);
        }
    }, [logger]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    const filteredQueue = useMemo(() =>
        queue.filter(wo => {
            const ms = !searchTerm ||
                wo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                wo.asset_id?.toLowerCase().includes(searchTerm.toLowerCase());
            const mp = filterPriority === 'all' || wo.priority === filterPriority;
            return ms && mp;
        }),
        [queue, searchTerm, filterPriority]
    );

    const toggleSelect = useCallback((id) => {
        setSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);

    const handleDispatch = useCallback(async (wo) => {
        setDispatchStatus('thinking');
        setDispatchResult(null);
        logger?.info('Dispatching WO', { id: wo.id });
        try {
            const res = await fieldOpsApi.autoDispatch({
                sensorId: wo.asset_id || 'unknown',
                issueType: wo.category || 'general',
                severity: wo.priority || 'medium',
                lat: 25.0, lng: -77.0,
                workOrderId: wo.id,
            });
            setDispatchStatus('dispatched');
            setDispatchResult(res);
            onDispatch?.(res);
            toast({
                title:'🤖 AI Dispatched!',
                description:`→ ${res.dispatchDetails?.assignedTeam || 'Team'} · ETA ${res.dispatchDetails?.estimatedArrival || 'TBD'}`,
                status:'success', duration:4000, isClosable:true, position:'top-right',
            });
            await loadQueue();
        } catch (err) {
            setDispatchStatus('error');
            toast({ title:'Dispatch Failed', description:err.message, status:'error', duration:3000 });
        }
        setTimeout(() => setDispatchStatus('idle'), 5000);
    }, [logger, onDispatch, toast, loadQueue]);

    const handleBulkDispatch = useCallback(async () => {
        if (!selected.size) return;
        setIsBulkRunning(true);
        let ok = 0;
        for (const id of selected) {
            const wo = queue.find(w => w.id === id);
            if (!wo) continue;
            try {
                await fieldOpsApi.autoDispatch({
                    sensorId: wo.asset_id || 'unknown',
                    issueType: wo.category || 'general',
                    severity: wo.priority || 'medium',
                    lat: 25.0, lng: -77.0,
                    workOrderId: wo.id,
                });
                ok++;
                onDispatch?.({ workOrderId: id });
            } catch { /* swallow per-item errors */ }
        }
        setIsBulkRunning(false);
        setSelected(new Set());
        await loadQueue();
        toast({
            title:`✅ Bulk Complete`,
            description:`${ok}/${selected.size} dispatched successfully`,
            status: ok === selected.size ? 'success' : 'warning',
            duration:4000, isClosable:true, position:'top-right',
        });
    }, [selected, queue, onDispatch, toast, loadQueue]);

    return (
        <SectionCard accentColor="blue">
            <PanelHeader
                iconBg="blue.500" icon={MdSmartToy}
                title="AI Dispatch Queue"
                subtitle={`${filteredQueue.length} pending · ${selected.size} selected`}
                right={
                    <HStack spacing={2}>
                        <AIStatusBadge status={dispatchStatus} />
                        <Tooltip label="Refresh Queue" hasArrow>
                            <IconButton icon={<Icon as={MdRefresh} />} size="sm"
                                variant="ghost" onClick={loadQueue} borderRadius="full"
                                _hover={{ transform:'rotate(180deg)' }} transition="all 0.3s" />
                        </Tooltip>
                    </HStack>
                }
            />

            {/* Animated Result Banner */}
            <AnimatePresence>
                {dispatchResult && (
                    <MotionBox
                        initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }}
                        exit={{ height:0, opacity:0 }}
                        bgGradient="linear(to-r, green.500, green.600)" px={5} py={3}
                        transition={{ type:'spring', stiffness:200, damping:25 }}
                    >
                        <HStack spacing={3}>
                            <MotionBox
                                animate={{ rotate:[0,360] }}
                                transition={{ duration:0.6, ease:'easeOut' }}
                            >
                                <Icon as={MdCheckCircle} color="white" boxSize={5} />
                            </MotionBox>
                            <Box flex={1}>
                                <Text color="white" fontWeight="700" fontSize="sm">
                                    AI Dispatched → {dispatchResult.dispatchDetails?.assignedTeam}
                                </Text>
                                <Text color="whiteAlpha.800" fontSize="xs">
                                    ETA: {dispatchResult.dispatchDetails?.estimatedArrival}
                                    {dispatchResult.dispatchDetails?.confidence &&
                                        ` · Confidence: ${dispatchResult.dispatchDetails.confidence}%`}
                                </Text>
                            </Box>
                            {dispatchResult.dispatchDetails?.confidence && (
                                <CircularProgress
                                    value={dispatchResult.dispatchDetails.confidence}
                                    color="whiteAlpha.900" trackColor="whiteAlpha.300"
                                    size="42px" thickness="8px"
                                >
                                    <CircularProgressLabel color="white" fontSize="xs" fontWeight="800">
                                        {dispatchResult.dispatchDetails.confidence}%
                                    </CircularProgressLabel>
                                </CircularProgress>
                            )}
                        </HStack>
                    </MotionBox>
                )}
            </AnimatePresence>

            {/* Search + Filter */}
            <Box px={4} pt={3} pb={2}>
                <HStack spacing={2}>
                    <InputGroup size="sm" flex={1}>
                        <InputLeftElement pointerEvents="none">
                            <Icon as={SearchIcon} color="gray.400" boxSize={3} />
                        </InputLeftElement>
                        <Input
                            placeholder="Search work orders…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            bg={searchBg} borderRadius="lg" fontSize="xs"
                            _focus={{ borderColor:'blue.400', boxShadow:'0 0 0 1px var(--chakra-colors-blue-400)' }}
                        />
                        {searchTerm && (
                            <InputRightElement cursor="pointer" onClick={() => setSearchTerm('')}>
                                <Icon as={CloseIcon} color="gray.400" boxSize={2.5} />
                            </InputRightElement>
                        )}
                    </InputGroup>
                    <Select size="sm" value={filterPriority}
                        onChange={e => setFilterPriority(e.target.value)}
                        w="110px" borderRadius="lg" fontSize="xs"
                    >
                        <option value="all">All</option>
                        {Object.entries(PRIORITY_CFG).map(([k,v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </Select>
                </HStack>

                {filteredQueue.length > 0 && (
                    <HStack spacing={2} mt={2} justify="space-between">
                        <HStack spacing={1}>
                            <Button size="xs" variant="ghost" leftIcon={<Icon as={MdLibraryAddCheck} />}
                                onClick={() => setSelected(new Set(filteredQueue.map(w => w.id)))}
                                colorScheme="blue" fontSize="2xs">
                                All ({filteredQueue.length})
                            </Button>
                            {selected.size > 0 && (
                                <Button size="xs" variant="ghost" leftIcon={<Icon as={MdCheckBoxOutlineBlank} />}
                                    onClick={() => setSelected(new Set())}
                                    colorScheme="gray" fontSize="2xs">
                                    Clear
                                </Button>
                            )}
                        </HStack>
                        {selected.size > 0 && (
                            <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.springBounce}>
                                <Button size="xs" colorScheme="blue" leftIcon={<Icon as={MdBolt} />}
                                    isLoading={isBulkRunning}
                                    loadingText={`Dispatching ${selected.size}…`}
                                    onClick={handleBulkDispatch} borderRadius="full" fontSize="2xs"
                                    _hover={{ transform:'scale(1.05)', boxShadow:'0 0 16px var(--chakra-colors-blue-400)' }}>
                                    Bulk Dispatch ({selected.size})
                                </Button>
                            </MotionBox>
                        )}
                    </HStack>
                )}
            </Box>

            {/* Queue List */}
            <Box maxH="340px" overflowY="auto" css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-track': { background:'transparent' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {isLoadingQueue ? (
                    <VStack p={4} spacing={3}>
                        {[1,2,3].map(i => (
                            <Box key={i} w="full" p={3} borderRadius="xl">
                                <ShimmerSkeleton lines={2} />
                            </Box>
                        ))}
                    </VStack>
                ) : filteredQueue.length === 0 ? (
                    <EmptyState icon={MdCheckCircle} title="Queue is clear"
                        subtitle="All work orders dispatched" />
                ) : (
                    <VStack px={4} pb={4} spacing={1.5} align="stretch">
                        {filteredQueue.map((wo, i) => (
                            <DispatchQueueRow
                                key={wo.id}
                                wo={wo}
                                index={i}
                                isSelected={selected.has(wo.id)}
                                onToggle={toggleSelect}
                                onDispatch={handleDispatch}
                                isDispatching={dispatchStatus === 'thinking'}
                            />
                        ))}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TECH LIST / GRID ITEMS — enhanced with status glow + skill badges
// ─────────────────────────────────────────────────────────────────────────────
const TechListItem = memo(({ tech, index }) => {
    const rowHover   = useColorModeValue('gray.50','gray.750');
    const mutedColor = useColorModeValue('gray.500','gray.400');
    const cardBg     = useColorModeValue('white','gray.800');
    const sc   = STATUS_CFG[tech.status] || STATUS_CFG.offline;
    const name = [tech.first_name, tech.last_name].filter(Boolean).join(' ') || `Tech #${tech.id}`;
    return (
        <MotionBox {...ANIM.fadeInLeft} transition={ANIM.stagger(index)}>
            <HStack p={2.5} borderRadius="xl" _hover={{ bg:rowHover, transform:'translateX(3px)' }}
                transition="all 0.2s" spacing={3} role="group">
                <Box position="relative" flexShrink={0}>
                    <Avatar size="sm" name={name} bg={`${sc.color}.500`} color="white" />
                    <MotionBox
                        position="absolute" bottom={0} right={0} w={3} h={3}
                        bg={`${sc.color}.500`} borderRadius="full"
                        borderWidth="2px" borderColor={cardBg}
                        animate={tech.status === 'active' ? { scale:[1,1.3,1] } : {}}
                        transition={{ duration:2, repeat:Infinity }}
                    />
                </Box>
                <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>{name}</Text>
                    <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
                        {tech.team_name || 'Unassigned'} · {tech.current_lat ? '📍 Located' : '📍 Unknown'}
                    </Text>
                </Box>
                <Badge colorScheme={sc.color} fontSize="2xs" borderRadius="full" flexShrink={0}
                    boxShadow={tech.status === 'active' ? `0 0 8px var(--chakra-colors-${sc.color}-300)` : 'none'}>
                    {sc.label}
                </Badge>
            </HStack>
        </MotionBox>
    );
});

const TechGridItem = memo(({ tech, index }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const gridCardBg  = useColorModeValue('gray.50','gray.750');
    const sc   = STATUS_CFG[tech.status] || STATUS_CFG.offline;
    const name = [tech.first_name, tech.last_name].filter(Boolean).join(' ') || `Tech #${tech.id}`;
    return (
        <MotionBox
            {...ANIM.scaleIn} transition={ANIM.stagger(index)}
        >
            <Flex direction="column" align="center" gap={1.5}
                p={3} borderRadius="xl" bg={gridCardBg}
                borderWidth="1px" borderColor={borderColor}
                _hover={{ borderColor:`${sc.color}.300`, transform:'scale(1.03)',
                    boxShadow:`0 4px 16px var(--chakra-colors-${sc.color}-100)` }}
                transition="all 0.25s">
                <Box position="relative">
                    <Avatar size="md" name={name} bg={`${sc.color}.500`} color="white" />
                    <Box position="absolute" bottom={0} right={0} w={3} h={3}
                        bg={`${sc.color}.500`} borderRadius="full" borderWidth="2px" borderColor="white" />
                </Box>
                <Text fontSize="xs" fontWeight="700" noOfLines={1} textAlign="center">{name}</Text>
                <Badge colorScheme={sc.color} fontSize="2xs" borderRadius="full">{sc.label}</Badge>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// TECHNICIAN FEED PANEL (Enhanced)
// ─────────────────────────────────────────────────────────────────────────────
const TechnicianFeedPanel = ({ logger }) => {
    const [technicians, setTechnicians] = useState([]);
    const [isLoading,   setIsLoading]   = useState(true);
    const [viewMode,    setViewMode]    = useState('list');
    const [filterStatus,setFilterStatus]= useState('all');

    const load = useCallback(async () => {
        try {
            const res = await fieldOpsApi.getTechnicians();
            setTechnicians(res?.data || []);
        } catch (err) {
            logger?.error('Tech load failed', err);
        } finally {
            setIsLoading(false);
        }
    }, [logger]);

    useEffect(() => { load(); }, [load]);
    useInterval(load, 30000);

    const filtered = useMemo(() =>
        filterStatus === 'all' ? technicians : technicians.filter(t => t.status === filterStatus),
        [technicians, filterStatus]
    );
    const counts = useMemo(() =>
        technicians.reduce((a, t) => { a[t.status] = (a[t.status]||0)+1; return a; }, {}),
        [technicians]
    );
    const available = counts['active'] || 0;

    return (
        <SectionCard accentColor="green">
            <PanelHeader
                iconBg="green.500" icon={MdPerson}
                title="Field Technicians"
                subtitle="Live availability feed"
                right={
                    <HStack spacing={2}>
                        <PulseIndicator color="green" size={2} />
                        <Badge colorScheme="green" borderRadius="full" px={2} fontSize="xs"
                            boxShadow="0 0 8px var(--chakra-colors-green-300)">
                            {available} available
                        </Badge>
                        <IconButton
                            icon={<Icon as={viewMode==='list' ? MdViewModule : MdViewList} />}
                            size="xs" variant="ghost" borderRadius="full"
                            onClick={() => setViewMode(v => v==='list'?'grid':'list')}
                            _hover={{ transform:'rotate(90deg)' }} transition="all 0.3s"
                        />
                    </HStack>
                }
            />

            {/* Status filter with animated underline */}
            <HStack px={4} pt={3} pb={1} spacing={1} overflowX="auto">
                {['all','active','busy','offline'].map(s => {
                    const sc  = STATUS_CFG[s] || { color:'gray' };
                    const cnt = s==='all' ? technicians.length : (counts[s]||0);
                    return (
                        <Button key={s} size="xs"
                            variant={filterStatus===s ? 'solid' : 'ghost'}
                            colorScheme={filterStatus===s ? (sc.color||'blue') : 'gray'}
                            borderRadius="full" flexShrink={0} fontSize="2xs"
                            onClick={() => setFilterStatus(s)}
                            _hover={{ transform:'translateY(-1px)' }}
                            transition="all 0.2s">
                            {s==='all' ? 'All' : sc.label} ({cnt})
                        </Button>
                    );
                })}
            </HStack>

            <Box maxH="300px" overflowY="auto" px={4} pb={4} pt={2} css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {isLoading ? (
                    <VStack spacing={2}>
                        {[1,2,3].map(i => (
                            <HStack key={i} w="full" spacing={3}>
                                <Skeleton w="36px" h="36px" borderRadius="full" flexShrink={0} />
                                <ShimmerSkeleton lines={2} />
                            </HStack>
                        ))}
                    </VStack>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={MdGroups} title="No technicians"
                        subtitle="No technicians match the filter" />
                ) : viewMode === 'list' ? (
                    <VStack spacing={1} align="stretch">
                        {filtered.map((t, i) => <TechListItem key={t.id} tech={t} index={i} />)}
                    </VStack>
                ) : (
                    <SimpleGrid columns={2} spacing={2}>
                        {filtered.map((t, i) => <TechGridItem key={t.id} tech={t} index={i} />)}
                    </SimpleGrid>
                )}
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// INSIGHT CARD — enhanced with shimmer highlight + animated expand
// ─────────────────────────────────────────────────────────────────────────────
const InsightCard = memo(({ insight, index, isExpanded, onToggle }) => {
    const tc        = INSIGHT_CFG[insight.type] || INSIGHT_CFG.info;
    const insightBg = useColorModeValue(`${tc.color}.50`, `${tc.color}.900`);
    const titleCol  = useColorModeValue(`${tc.color}.700`, `${tc.color}.300`);
    const borderCol = useColorModeValue(`${tc.color}.200`, `${tc.color}.700`);
    const mutedColor= useColorModeValue('gray.500','gray.400');
    return (
        <MotionBox
            {...ANIM.fadeInUp} transition={ANIM.stagger(index, 0.07)}
        >
            <Box
                p={4} borderRadius="xl" borderWidth="1px" borderLeftWidth="4px"
                borderColor={borderCol} borderLeftColor={`${tc.color}.500`}
                bg={insightBg} cursor="pointer" onClick={onToggle}
                transition="all 0.25s"
                _hover={{ boxShadow:`0 4px 16px var(--chakra-colors-${tc.color}-100)`,
                    transform:'translateX(3px)' }}
            >
                <Flex justify="space-between" align="flex-start">
                    <HStack spacing={2} flex={1}>
                        <MotionBox
                            animate={insight.type === 'warning' || insight.type === 'error'
                                ? { scale:[1,1.2,1] } : {}}
                            transition={{ duration:2, repeat:Infinity }}
                        >
                            <Icon as={tc.icon} boxSize={4} color={`${tc.color}.500`} flexShrink={0} />
                        </MotionBox>
                        <Text fontSize="xs" fontWeight="700" color={titleCol}>{insight.title}</Text>
                    </HStack>
                    <HStack spacing={1}>
                        {insight.severity && (
                            <Badge colorScheme={tc.color} fontSize="2xs" borderRadius="full">
                                {insight.severity}
                            </Badge>
                        )}
                        <MotionBox animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration:0.2 }}>
                            <Icon as={MdExpandMore} boxSize={4} color={mutedColor} />
                        </MotionBox>
                    </HStack>
                </Flex>
                <Text fontSize="2xs" color={mutedColor} mt={1} lineHeight="1.5"
                    noOfLines={isExpanded ? 99 : 2}>
                    {insight.message}
                </Text>
                <AnimatePresence>
                    {isExpanded && insight.recommendations?.length > 0 && (
                        <MotionBox
                            initial={{ height:0, opacity:0 }}
                            animate={{ height:'auto', opacity:1 }}
                            exit={{ height:0, opacity:0 }}
                            transition={{ duration:0.25 }} mt={2}
                        >
                            <Divider mb={2} borderColor={`${tc.color}.200`} />
                            <VStack align="start" spacing={1.5}>
                                {insight.recommendations.map((rec, ri) => (
                                    <MotionBox key={ri}
                                        initial={{ opacity:0, x:-8 }}
                                        animate={{ opacity:1, x:0 }}
                                        transition={{ delay: ri * 0.08 }}
                                    >
                                        <HStack spacing={1.5}>
                                            <Icon as={CheckIcon} boxSize={2.5} color={`${tc.color}.500`} flexShrink={0} />
                                            <Text fontSize="2xs" color={mutedColor}>{rec}</Text>
                                        </HStack>
                                    </MotionBox>
                                ))}
                            </VStack>
                        </MotionBox>
                    )}
                </AnimatePresence>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// AI INSIGHTS PANEL (Enhanced)
// ─────────────────────────────────────────────────────────────────────────────
const AIInsightsPanel = ({ logger }) => {
    const [insights,   setInsights]   = useState([]);
    const [isLoading,  setIsLoading]  = useState(true);
    const [expanded,   setExpanded]   = useState(null);
    const [filterType, setFilterType] = useState('all');

    const borderColor = useColorModeValue('gray.200','gray.700');

    useEffect(() => {
        fieldOpsApi.getAiInsights()
            .then(res => {
                setInsights(res?.data || []);
                logger?.info('Insights loaded', { count: res?.data?.length });
            })
            .catch(err => {
                logger?.error('Insights load failed', err);
                setInsights([
                    { type:'info',    severity:'medium', title:'FlowSense AI Online',
                      message:'Dispatch engine monitoring field operations in real time. Neural routing active with 94.2% confidence score.',
                      recommendations:['Ensure technicians have the mobile app','Review zone boundaries monthly','Enable push notifications for critical WOs'] },
                    { type:'success', severity:'low',    title:'Routing Rules Active',
                      message:'Assignment rules configured and auto-routing incoming work orders. ML model last trained 2 hours ago.',
                      recommendations:['Test rules with sample WOs','Adjust priority weighting','Review rule hit rates weekly'] },
                    { type:'warning', severity:'medium', title:'Review SLA Thresholds',
                      message:'3 work orders have approached SLA limits in the last 24 hours. Predictive model suggests 2 more at risk.',
                      recommendations:['Check high-priority open WOs','Verify technician availability','Consider temporary resource allocation'] },
                ]);
            })
            .finally(() => setIsLoading(false));
    }, [logger]);

    const filtered = useMemo(() =>
        filterType === 'all' ? insights : insights.filter(i => i.type === filterType),
        [insights, filterType]
    );

    return (
        <SectionCard accentColor="purple">
            <PanelHeader
                iconBg="purple.500" icon={MdOutlineAutoAwesome}
                title="FlowSense AI Insights"
                subtitle="Real-time ML recommendations"
                right={
                    <HStack spacing={2}>
                        <Select size="xs" value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            w="90px" borderRadius="lg" fontSize="2xs">
                            <option value="all">All</option>
                            {Object.keys(INSIGHT_CFG).map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </Select>
                        <Badge colorScheme="purple" borderRadius="full" px={2} fontSize="xs">
                            {isLoading ? '…' : filtered.length}
                        </Badge>
                    </HStack>
                }
            />
            <Box maxH="360px" overflowY="auto" p={4} css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {isLoading ? (
                    <VStack spacing={3}>
                        {[1,2].map(i => (
                            <Box key={i} p={4} w="full" borderRadius="xl"
                                borderWidth="1px" borderColor={borderColor}>
                                <ShimmerSkeleton lines={3} />
                            </Box>
                        ))}
                    </VStack>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={MdOutlineAutoAwesome} title="No insights"
                        subtitle="AI has no recommendations right now" />
                ) : (
                    <VStack spacing={2} align="stretch">
                        {filtered.slice(0,6).map((insight, i) => (
                            <InsightCard
                                key={i} insight={insight} index={i}
                                isExpanded={expanded === i}
                                onToggle={() => setExpanded(expanded===i ? null : i)}
                            />
                        ))}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SYSTEM HEALTH PANEL (Fully Connected to Real Backend Telemetry)
// ─────────────────────────────────────────────────────────────────────────────
const SystemHealthPanel = ({ logger }) => {
    // Start with a loading/skeleton state
    const [services, setServices] = useState([
        { label:'AI Dispatch Engine',  key:'dispatcher', healthy:true, lat:'--' },
        { label:'Zone Spatial Query',  key:'zones',      healthy:true, lat:'--' },
        { label:'Assignment Rules',    key:'rules',      healthy:true, lat:'--' },
        { label:'Technician Tracker',  key:'techs',      healthy:true, lat:'--' },
        { label:'FCM Notifications',   key:'fcm',        healthy:true, lat:'--' },
        { label:'ML Model Service',    key:'ml',         healthy:true, lat:'--' },
    ]);

    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const trackColor  = useColorModeValue('gray.100','gray.700');
    const rowHoverBg  = useColorModeValue('gray.50', 'gray.750');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    // Fetch the real latency metrics from our new backend endpoint
    const fetchHealth = useCallback(async () => {
        try {
            const res = await fieldOpsApi.getSystemHealth();
            if (Array.isArray(res?.data)) { // --- UPDATE: a non-array payload used to crash the whole page (services.filter)
                setServices(res.data);
            }
        } catch (err) {
            logger?.error('System Health check failed', err);
            // If the backend fails completely, mark everything as DOWN
            setServices(prev => prev.map(s => ({ ...s, healthy: false, lat: 'ERR' })));
        }
    }, [logger]);

    // Initial load and live 15-second polling
    useEffect(() => { fetchHealth(); }, [fetchHealth]);
    useInterval(fetchHealth, 15000); 

    const healthyCount = services.filter(s => s.healthy).length;
    const healthPct    = Math.round((healthyCount / services.length) * 100) || 0;

    return (
        <SectionCard accentColor="teal">
            <PanelHeader
                iconBg="teal.500" icon={MdNetworkCheck}
                title="System Health"
                subtitle="FlowSense service status"
                right={
                    <MotionBox
                        animate={{ rotate: healthPct === 100 ? 0 : [0,5,-5,0] }}
                        transition={{ duration:2, repeat: healthPct < 100 ? Infinity : 0 }}
                    >
                        <CircularProgress value={healthPct}
                            color={healthPct===100 ? 'green.400':'orange.400'}
                            size="48px" trackColor={trackColor}>
                            <CircularProgressLabel fontSize="xs" fontWeight="800">{healthPct}%</CircularProgressLabel>
                        </CircularProgress>
                    </MotionBox>
                }
            />
            <VStack px={4} py={3} spacing={1} align="stretch">
                {services.map((svc, i) => (
                    <MotionBox key={svc.key}
                        {...ANIM.fadeInLeft} transition={ANIM.stagger(i, 0.06)}
                    >
                        <Flex align="center" justify="space-between" py={1.5}
                            _hover={{ bg: rowHoverBg }}
                            transition="all 0.15s" borderRadius="lg" px={2}>
                            <HStack spacing={2}>
                                <MotionBox
                                    animate={svc.healthy ? {} : { scale:[1,1.3,1] }}
                                    transition={{ duration:1.5, repeat:Infinity }}
                                >
                                    <Box w={2} h={2} borderRadius="full"
                                        bg={svc.healthy ? 'green.500':'red.500'}
                                        boxShadow={svc.healthy ? '0 0 6px var(--chakra-colors-green-400)':'0 0 6px var(--chakra-colors-red-400)'} />
                                </MotionBox>
                                <Text fontSize="xs" fontWeight={svc.healthy ? '600':'400'}>{svc.label}</Text>
                            </HStack>
                            <HStack spacing={2}>
                                {svc.lat === '--' ? (
                                    <Spinner size="xs" color="gray.400" />
                                ) : (
                                    <Text fontSize="2xs" color={mutedColor} fontFamily="mono">{svc.lat}</Text>
                                )}
                                <Badge colorScheme={svc.healthy ? 'green':'red'} fontSize="2xs" borderRadius="full">
                                    {svc.healthy ? 'OK':'DOWN'}
                                </Badge>
                            </HStack>
                        </Flex>
                    </MotionBox>
                ))}
            </VStack>
            <Box px={4} pb={4} pt={1}>
                <Divider mb={3} borderColor={borderColor} />
                <Flex justify="space-between" mb={1}>
                    <Text fontSize="2xs" color={mutedColor} fontWeight="600">OVERALL HEALTH</Text>
                    <Text fontSize="2xs" fontWeight="800"
                        color={healthPct===100 ? 'green.500':'orange.400'}>
                        {healthyCount}/{services.length} services
                    </Text>
                </Flex>
                <Progress value={healthPct} colorScheme={healthPct===100 ? 'green':'orange'}
                    size="sm" borderRadius="full" bg={trackColor}
                    hasStripe isAnimated />
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// PERFORMANCE METRICS PANEL (Enhanced with animated KPI tiles)
// ─────────────────────────────────────────────────────────────────────────────
const PerformancePanel = ({ logger }) => {
    const [metrics,   setMetrics]   = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [period,    setPeriod]    = useState('7d');

    const borderColor = useColorModeValue('gray.200','gray.700');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const greenBg     = useColorModeValue('green.50','green.900');
    const blueBg      = useColorModeValue('blue.50','blue.900');
    const orangeBg    = useColorModeValue('orange.50','orange.900');
    const purpleBg    = useColorModeValue('purple.50','purple.900');
    const redBg       = useColorModeValue('red.50','red.900');
    const tealBg      = useColorModeValue('teal.50','teal.900');

    useEffect(() => {
        setIsLoading(true);
        // Pass the period ('1d', '7d', '30d') to the backend
        fieldOpsApi.getPerformance({ period })
            .then(res => setMetrics(res?.data || null))
            .catch(() => setMetrics({
                avgResponseTime: 0, completionRate: 0, slaBreaches: 0,
                aiAccuracy: 0,      dispatchedToday: 0, avgDispatchTime: '0m',
                techUtilization: 0, pendingBacklog: 0,
            }))
            .finally(() => setIsLoading(false));
    }, [period]);

    const kpis = useMemo(() => metrics ? [
        { label:'Avg Response',    value: metrics.avgResponseTime, unit:'min', color:'blue',   bg:blueBg,   icon:MdSpeed       },
        { label:'Completion Rate', value:`${metrics.completionRate}`, unit:'%', color:'green', bg:greenBg,  icon:MdCheckCircle },
        { label:'SLA Breaches',    value: metrics.slaBreaches,    unit:'',   color:'red',    bg:redBg,    icon:MdWarning      },
        { label:'AI Accuracy',     value:`${metrics.aiAccuracy}`,  unit:'%', color:'purple', bg:purpleBg, icon:MdPsychology   },
        { label:'Dispatched Today',value: metrics.dispatchedToday, unit:'',   color:'teal',   bg:tealBg,   icon:MdSend         },
        { label:'Avg Dispatch',    value: metrics.avgDispatchTime, unit:'',   color:'blue',   bg:blueBg,   icon:MdTimeline     },
        { label:'Tech Utilization',value:`${metrics.techUtilization}`, unit:'%', color:'orange', bg:orangeBg, icon:MdEngineering },
        { label:'Backlog',         value: metrics.pendingBacklog,  unit:'WO', color:'gray',   bg:blueBg,   icon:MdStorage      },
    ] : [], [metrics, greenBg, blueBg, orangeBg, purpleBg, redBg, tealBg]);

    return (
        <SectionCard accentColor="orange">
            <PanelHeader
                iconBg="orange.500" icon={MdBarChart}
                title="Performance Metrics"
                subtitle={`Field ops KPIs — last ${period}`}
                right={
                    <Select size="xs" value={period} onChange={e => setPeriod(e.target.value)}
                        w="80px" borderRadius="lg" fontSize="2xs">
                        <option value="1d">Today</option>
                        <option value="7d">7 days</option>
                        <option value="30d">30 days</option>
                    </Select>
                }
            />
            {isLoading ? (
                <Box p={4}><ShimmerSkeleton lines={6} /></Box>
            ) : (
                <SimpleGrid columns={2} spacing={3} p={4}>
                    {kpis.map((kpi, idx) => (
                        <MotionBox key={kpi.label}
                            {...ANIM.scaleIn} transition={ANIM.stagger(idx, 0.05)}
                        >
                            <Box p={3} bg={kpi.bg} borderRadius="xl"
                                borderWidth="1px" borderColor={borderColor}
                                _hover={{ transform:'scale(1.03)',
                                    boxShadow:`0 4px 16px var(--chakra-colors-${kpi.color}-100)` }}
                                transition="all 0.25s">
                                <HStack justify="space-between" mb={1}>
                                    <Text fontSize="2xs" color={mutedColor} fontWeight="600"
                                        textTransform="uppercase" letterSpacing="wider" noOfLines={1}>
                                        {kpi.label}
                                    </Text>
                                    <Icon as={kpi.icon} boxSize={3.5} color={`${kpi.color}.400`} />
                                </HStack>
                                <HStack spacing={1} align="baseline">
                                    <Text fontSize="2xl" fontWeight="900" color={`${kpi.color}.500`} lineHeight="1">
                                        {kpi.value}
                                    </Text>
                                    {kpi.unit && <Text fontSize="2xs" color={mutedColor}>{kpi.unit}</Text>}
                                </HStack>
                            </Box>
                        </MotionBox>
                    ))}
                </SimpleGrid>
            )}
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// AUDIT LOG ROW — extracted with enhanced animation
// ─────────────────────────────────────────────────────────────────────────────
const AuditLogRow = memo(({ log, index }) => {
    const rowHover   = useColorModeValue('gray.50','gray.750');
    const mutedColor = useColorModeValue('gray.500','gray.400');
    const levelCfg   = {
        success:{ color:'green',  icon:MdCheckCircle },
        info:   { color:'blue',   icon:MdInfo        },
        warning:{ color:'orange', icon:MdWarning     },
        error:  { color:'red',    icon:MdError       },
    };
    const lc = levelCfg[log.level] || levelCfg.info;
    return (
        <MotionBox {...ANIM.fadeInLeft} transition={ANIM.stagger(index, 0.03)}>
            <Flex py={2.5} px={2} gap={3} align="flex-start"
                _hover={{ bg:rowHover, transform:'translateX(2px)' }}
                transition="all 0.15s" borderRadius="lg">
                <MotionBox
                    animate={log.level === 'error' ? { scale:[1,1.2,1] } : {}}
                    transition={{ duration:2, repeat:Infinity }}
                >
                    <Icon as={lc.icon} boxSize={4} color={`${lc.color}.500`} flexShrink={0} mt={0.5} />
                </MotionBox>
                <Box flex={1} minW={0}>
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="xs" fontWeight="700" noOfLines={1}>{log.action}</Text>
                        <Badge colorScheme={lc.color} fontSize="2xs" borderRadius="full">{log.target}</Badge>
                    </HStack>
                    <Text fontSize="2xs" color={mutedColor} noOfLines={1} mt={0.5}>{log.detail}</Text>
                </Box>
                <VStack align="end" spacing={0} flexShrink={0}>
                    <Text fontSize="2xs" color={mutedColor} fontFamily="mono">
                        {new Date(log.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                    </Text>
                    <Text fontSize="2xs" color={mutedColor}>{log.actor}</Text>
                </VStack>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// AUDIT LOG PANEL (Enhanced)
// ─────────────────────────────────────────────────────────────────────────────
const AuditLogPanel = ({ logger }) => {
    const [logs,      setLogs]      = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter,    setFilter]    = useState('all');

    const borderColor = useColorModeValue('gray.200','gray.700');

    useEffect(() => {
        fieldOpsApi.getAuditLog(30)
            .then(res => setLogs(res?.data || []))
            .catch(() => {
                const now = Date.now();
                setLogs([
                    { id:'a1', action:'AI Dispatch',   actor:'System AI',   target:'WO-0045', level:'success', detail:'Routed to Team Alpha · Priority: High · Confidence: 94%',    timestamp: new Date(now-5*60000).toISOString()  },
                    { id:'a2', action:'Rule Created',  actor:'Admin John',  target:'Rule-12', level:'info',    detail:'Zone: Downtown · Type: Emergency · Auto-enabled',          timestamp: new Date(now-20*60000).toISOString() },
                    { id:'a3', action:'Zone Updated',  actor:'Admin Sara',  target:'Zone-3',  level:'info',    detail:'Boundary extended by 2.4 km² · Reindexed',              timestamp: new Date(now-35*60000).toISOString() },
                    { id:'a4', action:'Tech Assigned', actor:'System AI',   target:'WO-0041', level:'success', detail:'Assigned to M. Torres · Confidence 91% · ETA 12min',   timestamp: new Date(now-50*60000).toISOString() },
                    { id:'a5', action:'SLA Breach',    actor:'System',      target:'WO-0038', level:'warning', detail:'Response exceeded 4hr threshold · Escalated',          timestamp: new Date(now-75*60000).toISOString() },
                    { id:'a6', action:'Bulk Dispatch', actor:'Admin John',  target:'5 WOs',   level:'success', detail:'5 work orders dispatched · Avg confidence 89%',    timestamp: new Date(now-90*60000).toISOString() },
                    { id:'a7', action:'Rule Disabled', actor:'Admin Sara',  target:'Rule-7',  level:'warning', detail:'Seasonal zone rule deactivated · Expires Q2',            timestamp: new Date(now-120*60000).toISOString()},
                    { id:'a8', action:'AI Retrain',    actor:'FlowSense AI',target:'Model',   level:'info',    detail:'Dispatcher retrained · 87.2% accuracy · +2.1%',   timestamp: new Date(now-180*60000).toISOString()},
                ]);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const filtered = useMemo(() =>
        filter === 'all' ? logs : logs.filter(l => l.level === filter),
        [logs, filter]
    );

    return (
        <SectionCard accentColor="gray">
            <PanelHeader
                iconBg="gray.600" icon={MdAudit}
                title="Audit Log"
                subtitle="Field operations activity trail"
                right={
                    <HStack spacing={2}>
                        <Select size="xs" value={filter} onChange={e => setFilter(e.target.value)}
                            w="90px" borderRadius="lg" fontSize="2xs">
                            <option value="all">All</option>
                            <option value="success">Success</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                        </Select>
                        <Badge colorScheme="gray" borderRadius="full" px={2} fontSize="xs">
                            {filtered.length}
                        </Badge>
                    </HStack>
                }
            />
            <Box maxH="300px" overflowY="auto" css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {isLoading ? (
                    <VStack p={4} spacing={2}>
                        {[1,2,3].map(i => <ShimmerSkeleton key={i} lines={2} />)}
                    </VStack>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={MdAudit} title="No log entries"
                        subtitle="Activity will appear here" />
                ) : (
                    <VStack p={3} spacing={0} align="stretch"
                        divider={<Divider borderColor={borderColor} />}>
                        {filtered.map((log, i) => (
                            <AuditLogRow key={log.id || i} log={log} index={i} />
                        ))}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// DISPATCH HISTORY ROW + PANEL (Enhanced)
// ─────────────────────────────────────────────────────────────────────────────
const DispatchHistoryRow = memo(({ dispatch: d, index }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const rowHover    = useColorModeValue('gray.50','gray.750');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    return (
        <MotionBox {...ANIM.slideIn} transition={ANIM.stagger(index)}>
            <Flex p={3} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
                _hover={{ borderColor:'orange.300', bg:rowHover, transform:'translateX(2px)' }}
                transition="all 0.2s" align="center" gap={3}>
                <MotionBox
                    initial={{ scale:0, rotate:-180 }}
                    animate={{ scale:1, rotate:0 }}
                    transition={{ type:'spring', stiffness:200 }}
                >
                    <Icon as={MdCheckCircle} color="green.500" boxSize={5} flexShrink={0} />
                </MotionBox>
                <Box flex={1} minW={0}>
                    <Text fontSize="xs" fontWeight="700" noOfLines={1}>
                        → {d.dispatchDetails?.assignedTeam || 'Team assigned'}
                    </Text>
                    <HStack spacing={2} mt={0.5}>
                        <Text fontSize="2xs" color={mutedColor}>
                            ETA: {d.dispatchDetails?.estimatedArrival || 'TBD'}
                        </Text>
                        {d.timestamp && (
                            <>
                                <Text fontSize="2xs" color={mutedColor}>·</Text>
                                <Text fontSize="2xs" color={mutedColor}>
                                    {d.timestamp.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                                </Text>
                            </>
                        )}
                    </HStack>
                </Box>
                <Badge colorScheme="green" fontSize="2xs" borderRadius="full" flexShrink={0}
                    boxShadow="0 0 6px var(--chakra-colors-green-300)">Done</Badge>
            </Flex>
        </MotionBox>
    );
});

const DispatchHistoryPanel = ({ history }) => {
    return (
        <SectionCard accentColor="orange">
            <PanelHeader
                iconBg="orange.500" icon={MdHistory}
                title="Recent Dispatches"
                subtitle="AI-routed assignments this session"
                right={
                    <Badge colorScheme="orange" borderRadius="full" px={2} fontSize="xs"
                        boxShadow={history.length > 0 ? '0 0 8px var(--chakra-colors-orange-300)' : 'none'}>
                        {history.length}
                    </Badge>
                }
            />
            <Box maxH="300px" overflowY="auto" css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {history.length === 0 ? (
                    <EmptyState icon={MdSend} title="No dispatches yet"
                        subtitle="Use AI Dispatch to route work orders" />
                ) : (
                    <VStack p={3} spacing={1.5} align="stretch">
                        {history.map((d, i) => (
                            <DispatchHistoryRow key={i} dispatch={d} index={i} />
                        ))}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// QUICK REFERENCE PANEL (Enhanced with staggered animations)
// ─────────────────────────────────────────────────────────────────────────────
const QuickReferencePanel = ({ showQuickRef, setShowQuickRef }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const headingColor= useColorModeValue('gray.800','white');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const headerHover = useColorModeValue('gray.50','gray.750');
    const iconBg      = useColorModeValue('blue.50','blue.900');
    const alertBg     = useColorModeValue('blue.50','blue.900');
    const itemHoverBg = useColorModeValue('gray.50','gray.700');

    const steps = useMemo(() => [
        { n:1, icon:MdGroups,       color:'blue',   text:'Create teams and assign team leads in the Team Management tab.'          },
        { n:2, icon:MdLocationOn,   color:'green',  text:'Draw geographic zones using the interactive map in Assignment Zones.'   },
        { n:3, icon:MdRule,         color:'purple', text:'Set up routing rules to auto-assign work orders by zone and type.'      },
        { n:4, icon:MdEngineering,  color:'orange', text:'Assign skills to technicians for intelligent AI-based matching.'        },
        { n:5, icon:MdSmartToy,     color:'teal',   text:'Enable AI Dispatch to automatically route pending work orders.'         },
        { n:6, icon:MdNetworkCheck, color:'cyan',   text:'Monitor system health and dispatch history in the command center.'      },
    ], []);

    return (
        <GlassCard hoverLift={false}>
            <Flex justify="space-between" align="center" p={4}
                borderBottomWidth={showQuickRef ? '1px' : '0'} borderColor={borderColor}
                cursor="pointer" onClick={() => setShowQuickRef(!showQuickRef)}
                _hover={{ bg:headerHover }} transition="all 0.2s" borderRadius="2xl">
                <HStack spacing={3}>
                    <MotionBox
                        animate={{ rotate: showQuickRef ? 360 : 0 }}
                        transition={{ duration:0.4 }}
                    >
                        <Box bg={iconBg} p={2} borderRadius="lg">
                            <Icon as={FiInfo} color="blue.500" boxSize={5} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Text fontWeight="700" fontSize="sm" color={headingColor}>Quick Reference Guide</Text>
                        <Text fontSize="xs" color={mutedColor}>Setup steps for FlowSense field operations</Text>
                    </Box>
                </HStack>
                <MotionBox animate={{ rotate: showQuickRef ? 180 : 0 }} transition={{ duration:0.2 }}>
                    <Icon as={MdExpandMore} boxSize={5} color={mutedColor} />
                </MotionBox>
            </Flex>

            <Collapse in={showQuickRef} animateOpacity>
                <Box p={5}>
                    <SimpleGrid columns={{ base:1, md:2 }} spacing={3} mb={4}>
                        {steps.map(({ n, icon, color, text }) => (
                            <MotionBox key={n}
                                initial={{ opacity:0, x:-15 }} animate={{ opacity:1, x:0 }}
                                transition={{ delay: n * 0.06 }}
                                whileHover={{ x:4, boxShadow:'sm' }}
                            >
                                <HStack p={3} borderRadius="xl" _hover={{ bg:itemHoverBg }}
                                    transition="all 0.2s" spacing={3}>
                                    <Flex w="28px" h="28px" bg={`${color}.500`} color="white"
                                        borderRadius="lg" justify="center" align="center"
                                        fontWeight="800" fontSize="xs" flexShrink={0}
                                        boxShadow={`0 4px 12px var(--chakra-colors-${color}-200)`}>
                                        {n}
                                    </Flex>
                                    <Icon as={icon} color={`${color}.500`} boxSize={4} flexShrink={0} />
                                    <Text fontSize="sm" color={mutedColor}>{text}</Text>
                                </HStack>
                            </MotionBox>
                        ))}
                    </SimpleGrid>

                    <Alert status="info" variant="subtle" borderRadius="xl" bg={alertBg}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle fontSize="sm">Pro Tip — FlowSense AI</AlertTitle>
                            <AlertDescription fontSize="xs">
                                Configure zones first, then create assignment rules referencing them.
                                The AI dispatch engine uses both to find the optimal technician
                                based on location, skills, and current workload.
                            </AlertDescription>
                        </Box>
                    </Alert>
                </Box>
            </Collapse>
        </GlassCard>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ENHANCED TAB BUTTON (with animated active indicator)
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedTab = memo(({ tab, index, isActive, onClick }) => {
    const inactiveColor  = useColorModeValue('gray.700','gray.300');
    const hoverBg        = useColorModeValue('gray.100','gray.700');
    const iconInactiveBg = useColorModeValue('gray.100','gray.600');

    return (
        <MotionBox
            {...ANIM.fadeInDown} transition={ANIM.stagger(index, 0.08)}
            whileTap={{ scale:0.97 }}
        >
            <Tab
                onClick={onClick}
                bg={isActive ? 'blue.500' : 'transparent'}
                color={isActive ? 'white' : inactiveColor}
                _selected={{ bg:'blue.500', color:'white', boxShadow:'lg' }}
                _hover={{ bg: isActive ? 'blue.500' : hoverBg }}
                mr={2} px={5} py={3} borderRadius="xl" transition="all 0.25s"
                whiteSpace="nowrap" position="relative" overflow="hidden"
            >
                {isActive && (
                    <MotionBox
                        layoutId="activeTabBg"
                        position="absolute" inset={0} bg="whiteAlpha.150" borderRadius="xl"
                        transition={{ type:'spring', stiffness:400, damping:30 }}
                    />
                )}
                <HStack spacing={3} position="relative" zIndex={1}>
                    <MotionBox
                        animate={isActive ? { rotate:[0,10,-10,0] } : {}}
                        transition={{ duration:0.5 }}
                    >
                        <Box bg={isActive ? 'whiteAlpha.300' : iconInactiveBg} p={1.5} borderRadius="md">
                            <Icon as={tab.icon} boxSize={4} />
                        </Box>
                    </MotionBox>
                    <VStack align="start" spacing={0}>
                        <Text fontWeight="700" fontSize="sm">{tab.label}</Text>
                        <AnimatePresence>
                            {isActive && (
                                <MotionBox initial={{ opacity:0, height:0 }}
                                    animate={{ opacity:1, height:'auto' }}
                                    exit={{ opacity:0, height:0 }}>
                                    <Text fontSize="2xs" color="blue.100" opacity={0.85}>
                                        {tab.description}
                                    </Text>
                                </MotionBox>
                            )}
                        </AnimatePresence>
                    </VStack>
                    {tab.badge && (
                        <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.springBounce}>
                            <Badge colorScheme={tab.badgeColor||'green'} borderRadius="full" fontSize="2xs">
                                {tab.badge}
                            </Badge>
                        </MotionBox>
                    )}
                </HStack>
            </Tab>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG LOG ROW — extracted
// ─────────────────────────────────────────────────────────────────────────────
const DebugLogRow = memo(({ log }) => {
    const rowBg = useColorModeValue('gray.100','gray.700');
    return (
        <Box p={2} bg={rowBg} borderRadius="md" fontSize="xs" fontFamily="mono">
            <Flex justify="space-between" mb={1}>
                <Badge colorScheme={
                    log.level==='error'   ? 'red'    :
                    log.level==='warn'    ? 'orange' :
                    log.level==='success' ? 'green'  :
                    log.level==='info'    ? 'blue'   : 'gray'
                } fontSize="10px">{log.level.toUpperCase()}</Badge>
                <Text color="gray.500" fontSize="10px">
                    {new Date(log.timestamp).toLocaleTimeString()}
                </Text>
            </Flex>
            <Text fontWeight="600">{log.message}</Text>
            {log.data && (
                <Text color="gray.500" fontSize="10px" mt={0.5}>
                    {JSON.stringify(log.data).substring(0,120)}
                </Text>
            )}
        </Box>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// TAB PANEL INNER HEADER — safe for hooks
// ─────────────────────────────────────────────────────────────────────────────
const TabPanelHeader = memo(({ tab, stats }) => {
    const headingColor = useColorModeValue('gray.800','white');
    const mutedColor   = useColorModeValue('gray.500','gray.400');
    const borderColor  = useColorModeValue('gray.200','gray.700');
    const iconBg       = useColorModeValue(`${tab.color}.50`, `${tab.color}.900`);
    return (
        <>
            <HStack spacing={3} mb={5} pt={2}>
                <MotionBox
                    whileHover={{ rotate:8, scale:1.1 }}
                    transition={{ type:'spring', stiffness:400 }}
                >
                    <Box bg={iconBg} p={2} borderRadius="lg">
                        <Icon as={tab.icon} color={`${tab.color}.500`} boxSize={5} />
                    </Box>
                </MotionBox>
                <Box>
                    <Text fontWeight="800" fontSize="md" color={headingColor}>{tab.label}</Text>
                    <Text fontSize="xs" color={mutedColor}>{tab.description}</Text>
                </Box>
                {stats[tab.statKey] != null && (
                    <MotionBox initial={{ scale:0 }} animate={{ scale:1 }} transition={ANIM.springBounce} ml="auto">
                        <Badge colorScheme={tab.color} borderRadius="full" px={3}>
                            {stats[tab.statKey]} records
                        </Badge>
                    </MotionBox>
                )}
            </HStack>
            <Divider mb={5} borderColor={borderColor} />
        </>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT (Enhanced with performance + animations)
// ─────────────────────────────────────────────────────────────────────────────
const FieldOperationsPage = () => {
    const logger = useRef(new DebugLogger('FieldOperationsPage')).current;

    // ── State ─────────────────────────────────────────────────────────────────
    const [activeTab,        setActiveTab]       = useState(0);
    const [isLoading,        setIsLoading]       = useState(false);
    const [isRefreshing,     setIsRefreshing]    = useState(false);
    const [showQuickRef,     setShowQuickRef]    = useState(false);
    const [debugLogs,        setDebugLogs]       = useState([]);
    const [debugFilter,      setDebugFilter]     = useState('all');
    const [stats,            setStats]           = useState({ teams:null, zones:null, rules:null, technicians:null });
    const [statsLoaded,      setStatsLoaded]     = useState(false);
    const [lastRefresh,      setLastRefresh]     = useState(new Date());
    const [autoRefresh,      setAutoRefresh]     = useState(true);
    const [dispatchHistory,  setDispatchHistory] = useState([]);
    // --- UPDATE
    const [wsStatus,         setWsStatus]        = useState(() => (webSocketService.getStatus ? webSocketService.getStatus().status : 'idle'));
    const statsDebounce = useRef(null);
    // --- END UPDATE

    // ── Hooks ─────────────────────────────────────────────────────────────────
    const toast       = useToast();
    const isMobile    = useBreakpointValue({ base:true, md:false });
    const {
        isOpen: isDebugOpen,
        onOpen: onDebugOpen,
        onClose: onDebugClose,
    } = useDisclosure();

    // ── Theme — all unconditional ────────────────────────────────────────────
    const bgColor        = useColorModeValue('gray.50','gray.900');
    const cardBg         = useColorModeValue('white','gray.800');
    const headingColor   = useColorModeValue('gray.800','white');
    const mutedColor     = useColorModeValue('gray.500','gray.400');
    const borderColor    = useColorModeValue('gray.200','gray.700');
    const tabListBg      = useColorModeValue('white','gray.800');
    const breadcrumbColor= useColorModeValue('gray.500','gray.400');
    const debugVStackBg  = useColorModeValue('gray.50','gray.900');
    const headerGradient = useColorModeValue(
        'linear(to-r, blue.600, purple.600)',
        'linear(to-r, blue.400, purple.500)'
    );
    const autoBadgeCardBg = useColorModeValue('white','gray.800');

    // ── Refs ──────────────────────────────────────────────────────────────────
    const isMounted = useRef(true);

    // ── Tab config ────────────────────────────────────────────────────────────
    const tabs = useMemo(() => [
        { label:'Team Management',  icon:MdGroups,      description:'Manage field teams',      component:<TeamManagementComponent />,          color:'blue',   statKey:'teams'       },
        { label:'Assignment Zones', icon:MdLocationOn,  description:'Zones / constituencies', component:<ZoneManagementComponent />,          color:'green',  statKey:'zones'       },
        { label:'Assignment Rules', icon:MdRule,        description:'Auto-routing engine',     component:<AssignmentRuleManagementComponent />, color:'purple', statKey:'rules'       },
        { label:'Technician Skills',icon:MdEngineering, description:'Skills & qualifications', component:<TechnicianSkillsManager />,          color:'orange', statKey:'technicians' },
    ], []);

    const statCards = useMemo(() => [
        { key:'teams',       label:'Active Teams',      icon:MdGroups,      color:'blue',   help:'Field operations teams', live:false },
        { key:'zones',       label:'Coverage Zones',    icon:MdLocationOn,  color:'green',  help:'Zones / constituencies', live:true  }, // --- UPDATE: live
        { key:'rules',       label:'Active Rules',      icon:MdRule,        color:'purple', help:'Auto-routing rules',     live:false },
        { key:'technicians', label:'Field Technicians', icon:MdEngineering, color:'orange', help:'Active in system',       live:true  },
    ], []);

    // ── Data loading ──────────────────────────────────────────────────────────
    const loadStats = useCallback(async () => {
        logger.perfStart('loadStats');
        logger.info('Fetching stats');
        try {
            const result = await fieldOpsApi.getStats();
            if (isMounted.current && result?.success) {
                setStats(result.data);
                setStatsLoaded(true);
                setLastRefresh(new Date());
                logger.success('Stats loaded', result.data);
                logger.perfEnd('loadStats');
            }
        } catch (err) {
            logger.error('Stats fetch failed', err);
            setStats({ teams:0, zones:0, rules:0, technicians:0 });
            setStatsLoaded(true);
            logger.perfEnd('loadStats');
        }
    }, [logger]);

    useEffect(() => {
        isMounted.current = true;
        logger.info('Page mounted — v4.0 Enhanced');
        loadStats();
        return () => { isMounted.current = false; };
    }, [loadStats, logger]);

    useInterval(() => { if (autoRefresh) loadStats(); }, 60000);

    useEffect(() => {
        const unsub = logger.subscribe(entry =>
            setDebugLogs(prev => [...prev.slice(-99), entry])
        );
        return unsub;
    }, [logger]);

    // --- UPDATE: WebSocket — refresh the stat cards when zones / work orders / locations change ---
    useEffect(() => {
        const bump = () => {
            clearTimeout(statsDebounce.current);
            statsDebounce.current = setTimeout(() => { if (isMounted.current) loadStats(); }, 2000);
        };
        const onStatus = (msg) => { if (isMounted.current && msg?.status) setWsStatus(msg.status); };
        const events = ['field_ops_zone_update', ...LIVE_EVENTS];
        events.forEach((e) => webSocketService.subscribe(e, bump));
        webSocketService.subscribe('status', onStatus);
        return () => {
            clearTimeout(statsDebounce.current);
            events.forEach((e) => webSocketService.unsubscribe(e, bump));
            webSocketService.unsubscribe('status', onStatus);
        };
    }, [loadStats]);
    // --- END UPDATE ---

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleTabChange = useCallback((index) => {
        logger.info('Tab changed', { to: index, tab: tabs[index]?.label });
        setActiveTab(index);
    }, [tabs, logger]);

    const handleRefresh = useCallback(async () => {
        setIsLoading(true);
        await loadStats();
        setTimeout(() => {
            if (isMounted.current) {
                setIsLoading(false);
                toast({
                    title:'✅ Refreshed',
                    description:`Updated at ${new Date().toLocaleTimeString()}`,
                    status:'success', duration:2000, position:'top-right',
                });
            }
        }, 600);
    }, [loadStats, toast]);

    // --- UPDATE: zone command center hooks ---
    const handleOpenTab = useCallback((key) => {
        const idx = { teams:0, zones:1, rules:2, skills:3, technicians:3 }[key];
        if (idx !== undefined) handleTabChange(idx);
    }, [handleTabChange]);
    const handleZoneAssign = useCallback(async (workOrderId, techId) => {
        const res = await fieldOpsApi.assignWorkOrder(workOrderId, techId);
        logger.success('Zone AI assignment', { workOrderId, techId });
        setDispatchHistory(prev => [{ workOrderId, assignedTo: techId, source:'zone_ai', timestamp:new Date() }, ...prev.slice(0,14)]);
        loadStats();
        return res;
    }, [logger, loadStats]);
    // --- END UPDATE ---

    const handleDispatchComplete = useCallback((result) => {
        logger.success('Dispatch completed', result);
        setDispatchHistory(prev => [{ ...result, timestamp:new Date() }, ...prev.slice(0,14)]);
        loadStats();
    }, [logger, loadStats]);

    const handleExport = useCallback(() => {
        const data = { exportedAt: new Date().toISOString(), stats, dispatchHistory };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type:'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `field-ops-${Date.now()}.json`; a.click();
        toast({ title:'Export complete', status:'success', duration:2000 });
    }, [stats, dispatchHistory, toast]);

    const filteredDebugLogs = useMemo(() => {
        const l = debugFilter === 'all'
            ? debugLogs
            : debugLogs.filter(d => d.level === debugFilter);
        return l.slice(-50).reverse();
    }, [debugLogs, debugFilter]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box pt={{ base:'10px', md:'10px' }} bg={bgColor} minH="100vh" pb={14}>
            {/* HEADER */}
            <MotionFlex initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.45 }} direction="column" mb={6}>
                <Breadcrumb spacing="8px" separator={<ChevronRightIcon color={breadcrumbColor} />} fontSize="sm" mb={4}>
                    <BreadcrumbItem><BreadcrumbLink href="/admin" color={breadcrumbColor} _hover={{ color:'blue.500' }}>Admin</BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage><BreadcrumbLink color={headingColor} fontWeight="600">Field Operations</BreadcrumbLink></BreadcrumbItem>
                </Breadcrumb>
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <HStack spacing={4}>
                        <MotionBox initial={{ scale:0 }} animate={{ scale:1, rotate:[0,360] }} transition={{ duration:0.65, type:'spring' }} bgGradient={headerGradient} p={3} borderRadius="xl" boxShadow="0 8px 24px rgba(66,99,235,0.35)">
                            <Icon as={MdDashboard} color="white" boxSize={7} />
                        </MotionBox>
                        <Box>
                            <HStack spacing={3} align="center">
                                <Heading as="h1" size={isMobile ? 'lg' : 'xl'} fontWeight="900" bgGradient={headerGradient} bgClip="text">Field Operations</Heading>
                                <MotionBox animate={{ y:[0,-3,0] }} transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }}>
                                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1} fontSize="xs" fontWeight="700" boxShadow="0 0 12px var(--chakra-colors-blue-200)">FlowSense AI</Badge>
                                </MotionBox>
                            </HStack>
                            <HStack spacing={3} mt={0.5}><Text color={mutedColor} fontSize="sm">Unified dispatch · routing · technician management</Text><HStack spacing={1.5} fontSize="xs" color={mutedColor}><PulseIndicator color={wsStatus === 'connected' ? 'green' : 'orange'} size={2} /><Text fontWeight="600">{wsStatus === 'connected' ? 'Live' : 'Polling'}</Text></HStack>{/* --- UPDATE: real WebSocket state */}</HStack>
                        </Box>
                    </HStack>
                    <HStack spacing={2}>
                        <HStack spacing={2} px={3} py={2} borderRadius="xl" borderWidth="1px" borderColor={borderColor} bg={autoBadgeCardBg} _hover={{ borderColor:'green.300' }} transition="all 0.2s">
                            <MotionBox animate={autoRefresh ? { scale:[1,1.3,1] } : {}} transition={{ duration:2, repeat:Infinity }}><Box w={2} h={2} borderRadius="full" bg={autoRefresh ? 'green.500':'gray.400'} boxShadow={autoRefresh ? '0 0 6px var(--chakra-colors-green-400)':'none'} /></MotionBox>
                            <Text fontSize="xs" fontWeight="600" color={mutedColor}>Auto</Text>
                            <Switch size="sm" isChecked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} colorScheme="green" />
                        </HStack>
                        {statsLoaded && <Text fontSize="xs" color={mutedColor} display={{ base:'none', lg:'block' }}>Updated {lastRefresh.toLocaleTimeString()}</Text>}
                        <Tooltip label="Refresh Data" hasArrow><MotionBox whileHover={{ scale:1.06, rotate:180 }} whileTap={{ scale:0.95 }} transition={{ duration:0.3 }}><IconButton icon={isRefreshing ? <Spinner size="sm" /> : <Icon as={MdRefresh} />} size="md" onClick={handleRefresh} isLoading={isLoading} variant="ghost" colorScheme="blue" borderRadius="full" /></MotionBox></Tooltip>
                        {process.env.NODE_ENV === 'development' && <Tooltip label="Debug Panel" hasArrow><IconButton icon={<Icon as={MdBugReport} />} size="md" onClick={onDebugOpen} variant="ghost" colorScheme="yellow" borderRadius="full" /></Tooltip>}
                        <Menu><MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />} size="md" variant="ghost" borderRadius="full" /><MenuList borderRadius="xl" boxShadow="xl"><MenuItem icon={<Icon as={MdDownload} />} onClick={handleExport} borderRadius="md">Export Data</MenuItem><MenuItem icon={<FiPrinter />} borderRadius="md">Print Report</MenuItem><MenuDivider /><MenuItem icon={<FiHelpCircle />} borderRadius="md">Help & Docs</MenuItem></MenuList></Menu>
                    </HStack>
                </Flex>
            </MotionFlex>

            {/* STAT CARDS */}
            <SimpleGrid columns={{ base:2, md:4 }} spacing={4} mb={6}>
                {statCards.map((card, i) => (
                    <MotionBox key={card.key} {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(i, 0.08), ...ANIM.smoothEase }}>
                        <StatCard label={card.label} value={stats[card.key]} icon={card.icon} color={card.color} helpText={card.help} isLoading={!statsLoaded} onClick={() => handleTabChange(statCards.findIndex(s => s.key === card.key))} isActive={activeTab === statCards.findIndex(s => s.key === card.key)} liveIndicator={card.live} />
                    </MotionBox>
                ))}
            </SimpleGrid>

            {/* --- UPDATE: ZONES / CONSTITUENCIES — LIVE OPERATIONS --- */}
            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.12, ...ANIM.smoothEase }}>
                <ZoneCommandCenter onAssign={handleZoneAssign} onOpenTab={handleOpenTab} />
            </MotionBox>
            {/* --- END UPDATE --- */}

            {/* COMMAND CENTER ROW 1 */}
            <Grid templateColumns={{ base:'1fr', lg:'1.4fr 1fr', xl:'1.4fr 1fr 1fr' }} gap={5} mb={5}>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.18, ...ANIM.smoothEase }}><AIDispatchPanel logger={logger} onDispatch={handleDispatchComplete} /></MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.24, ...ANIM.smoothEase }}><TechnicianFeedPanel logger={logger} /></MotionBox>
                <Box>
                    <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.30, ...ANIM.smoothEase }} h="100%">
                        <SystemHealthPanel logger={logger} />
                    </MotionBox>
                </Box>
            </Grid>

            {/* COMMAND CENTER ROW 2 */}
            <Grid templateColumns={{ base:'1fr', lg:'1fr 1fr', xl:'1fr 1fr 1fr' }} gap={5} mb={5}>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.32, ...ANIM.smoothEase }}><AIInsightsPanel logger={logger} /></MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.38, ...ANIM.smoothEase }}><PerformancePanel logger={logger} /></MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.44, ...ANIM.smoothEase }}><DispatchHistoryPanel history={dispatchHistory} /></MotionBox>
            </Grid>

            {/* AUDIT LOG */}
            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.50, ...ANIM.smoothEase }} mb={5}><AuditLogPanel logger={logger} /></MotionBox>

            {/* MANAGEMENT TABS */}
            <Tabs variant="unstyled" index={activeTab} onChange={handleTabChange} isLazy>
                <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.32, ...ANIM.smoothEase }}>
                    <Box bg={tabListBg} p={3} borderRadius="2xl" boxShadow="md" borderWidth="1px" borderColor={borderColor} mb={5} overflowX="auto" css={{ '&::-webkit-scrollbar': { height:'5px' }, '&::-webkit-scrollbar-track': { background:'transparent' }, '&::-webkit-scrollbar-thumb': { background:borderColor, borderRadius:'3px' } }}>
                        <TabList>{tabs.map((tab, i) => <EnhancedTab key={tab.label} tab={tab} index={i} isActive={activeTab === i} onClick={() => handleTabChange(i)} />)}</TabList>
                    </Box>
                </MotionBox>
                <TabPanels>
                    {tabs.map((tab, i) => (
                        <TabPanel key={tab.label} px={0}>
                            <AnimatePresence mode="wait">
                                <MotionBox key={`panel-${i}`} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}>
                                    <Box bg={cardBg} p={6} borderRadius="2xl" boxShadow="xl" borderWidth="2px" borderColor={borderColor} minH="500px" position="relative" overflow="hidden">
                                        <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={`linear(to-r, ${tab.color}.400, ${tab.color}.700)`} borderTopRadius="2xl" />
                                        <AnimatePresence>
                                            {isLoading && (
                                                <MotionBox initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} position="absolute" inset={0} bg="blackAlpha.200" borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" zIndex={10} backdropFilter="blur(4px)">
                                                    <VStack spacing={3}><Spinner size="xl" color={`${tab.color}.500`} thickness="4px" /><Text fontSize="sm" color={mutedColor}>Loading...</Text></VStack>
                                                </MotionBox>
                                            )}
                                        </AnimatePresence>
                                        <TabPanelHeader tab={tab} stats={stats} />
                                        {tab.component}
                                    </Box>
                                </MotionBox>
                            </AnimatePresence>
                        </TabPanel>
                    ))}
                </TabPanels>
            </Tabs>

            {/* QUICK REFERENCE */}
            <MotionBox {...ANIM.fadeInUp} transition={{ delay:0.55, ...ANIM.smoothEase }} mt={6}>
                <QuickReferencePanel showQuickRef={showQuickRef} setShowQuickRef={setShowQuickRef} />
            </MotionBox>

            {/* DEBUG DRAWER */}
            <Drawer isOpen={isDebugOpen} placement="right" onClose={onDebugClose} size="md">
                <DrawerOverlay backdropFilter="blur(4px)" />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdBugReport} color="yellow.500" /><Text>FlowSense Debug Panel</Text></HStack></DrawerHeader>
                    <DrawerBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Heading size="sm" mb={3}>Runtime State</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={2} fontSize="sm">
                                    {Object.entries({ teams: stats.teams, zones: stats.zones, rules: stats.rules, technicians: stats.technicians, activeTab: tabs[activeTab]?.label, autoRefresh: String(autoRefresh), lastRefresh: lastRefresh.toLocaleTimeString() }).map(([k,v]) => (
                                        <React.Fragment key={k}><Text fontSize="sm" fontWeight="600" color={mutedColor} textTransform="capitalize">{k}:</Text><Text fontSize="sm" fontFamily="mono">{String(v ?? '—')}</Text></React.Fragment>
                                    ))}
                                </Grid>
                            </Box>
                            <Divider />
                            <Box>
                                <Flex justify="space-between" align="center" mb={3}>
                                    <Heading size="sm">Logs ({filteredDebugLogs.length})</Heading>
                                    <HStack>
                                        <Select size="sm" value={debugFilter} onChange={e => setDebugFilter(e.target.value)} maxW="110px" borderRadius="md">
                                            {['all','debug','info','warn','error','success'].map(l => <option key={l} value={l}>{l}</option>)}
                                        </Select>
                                        <Button size="sm" colorScheme="red" variant="ghost" onClick={() => { logger.clear(); setDebugLogs([]); }}>Clear</Button>
                                    </HStack>
                                </Flex>
                                <VStack spacing={2} align="stretch" maxH="420px" overflowY="auto" bg={debugVStackBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                                    {filteredDebugLogs.length === 0 ? (
                                        <Text fontSize="xs" color={mutedColor} textAlign="center" py={4}>No logs yet</Text>
                                    ) : (
                                        filteredDebugLogs.map(log => <DebugLogRow key={log.id} log={log} />)
                                    )}
                                </VStack>
                            </Box>
                        </VStack>
                    </DrawerBody>
                    <DrawerFooter borderTopWidth="1px">
                        <Button size="sm" mr={3} leftIcon={<Icon as={MdDownload} />} onClick={() => { const blob = new Blob([logger.export()], { type:'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `debug-logs-${Date.now()}.json`; a.click(); }}>Export Logs</Button>
                        <Button size="sm" colorScheme="blue" onClick={onDebugClose}>Close</Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
        </Box>
    );
};

export default FieldOperationsPage;